import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import Logo from '@/components/Logo';

const RefundPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      {/* Header */}
      <motion.header 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="glass-card border-b border-white/10 sticky top-0 z-50"
      >
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Logo textClassName="text-2xl font-bold text-white" />
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-white hover:text-blue-300 hover:bg-white/10 transition-all"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </motion.header>

      {/* Content */}
      <div className="container mx-auto px-6 py-16 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="glass-card-white p-8 md:p-12 rounded-2xl"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Politique de Remboursement</h1>
          <p className="text-gray-600 mb-8">Dernière mise à jour : Décembre 2024</p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            {/* Company Info */}
            <section>
              <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 mb-6">
                <p className="font-semibold text-gray-900 mb-2">MARKEAP</p>
                <p className="text-gray-700">Propriété de MY DIGITAL IDENTITY LTD</p>
                <p className="text-gray-700">71-75 Shelton Street, Covent Garden</p>
                <p className="text-gray-700">London, United Kingdom, WC2H 9JQ</p>
              </div>
              <p>
                Cette Politique de Remboursement décrit les conditions dans lesquelles MY DIGITAL IDENTITY LTD 
                peut accorder des remboursements pour le service Markeap.
              </p>
            </section>

            {/* General Policy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Politique Générale</h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-xl mb-4">
                <p className="font-semibold text-gray-900 mb-2">⚠️ Important</p>
                <p className="text-gray-700">
                  En raison de la nature des services SaaS et du traitement immédiat de vos données, 
                  <strong> nous n'offrons généralement pas de remboursement pour les abonnements Markeap.</strong>
                </p>
              </div>
              <p>
                Une fois que vous vous abonnez à Markeap, vous avez immédiatement accès à toutes les fonctionnalités 
                de la plateforme. Les frais d'abonnement ne sont pas remboursables, sauf dans les cas spécifiques 
                décrits ci-dessous.
              </p>
            </section>

            {/* Paddle 14-Day Right to Cancel */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Droit de Rétractation de 14 Jours (Consommateurs)</h2>
              
              <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl mb-4">
                <p className="font-semibold text-gray-900 mb-2">✅ Droit de Rétractation</p>
                <p className="text-gray-700">
                  Si vous êtes un consommateur et sauf exception mentionnée ci-dessous, vous disposez d'un 
                  <strong> délai de 14 jours pour annuler votre commande et demander un remboursement sans avoir à justifier votre décision.</strong>
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.1 Période de Rétractation</h3>
              <p className="mb-4">
                La période de rétractation expire 14 jours après le jour suivant la finalisation de la transaction. 
                Pour respecter le délai de rétractation, il suffit que vous nous envoyiez votre communication 
                concernant l'exercice de votre droit de rétractation avant l'expiration de la période de 14 jours.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.2 Comment Exercer Votre Droit de Rétractation</h3>
              <p className="mb-4">
                Pour annuler votre commande, vous devez informer Paddle (notre processeur de paiement) de votre décision. 
                Pour garantir un traitement immédiat, veuillez le faire en contactant le support Paddle via votre tableau de bord 
                ou en nous contactant directement.
              </p>
              
              <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 mb-4">
                <p className="font-semibold text-gray-900 mb-3">📧 Modèle de Formulaire d'Annulation</p>
                <div className="bg-white p-4 rounded-lg border border-gray-200 font-mono text-sm">
                  <p className="mb-2">À : Paddle, Équipe Support Acheteurs</p>
                  <p className="mb-2">Objet : Annulation de commande</p>
                  <br />
                  <p className="mb-2">Je vous notifie par la présente que j'annule mon contrat portant sur la vente du service suivant :</p>
                  <p className="mb-2">- Produit/Service : Markeap [votre plan]</p>
                  <p className="mb-2">- Date de commande : [date]</p>
                  <p className="mb-2">- Nom du consommateur : [votre nom]</p>
                  <p className="mb-2">- Email : [votre email]</p>
                  <br />
                  <p>Cordialement,</p>
                  <p>[Signature]</p>
                </div>
              </div>

              <p className="mb-4">
                Vous pouvez également nous informer en utilisant ce modèle de formulaire d'annulation ou par toute 
                autre déclaration claire et sans ambiguïté via nos canaux de communication disponibles.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.3 Important - Services d'Abonnement</h3>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-xl mb-4">
                <p className="font-semibold text-gray-900 mb-2">⚠️ Note Importante</p>
                <p className="text-gray-700">
                  Votre droit de rétractation s'applique <strong>uniquement lors de l'abonnement initial</strong> et 
                  non lors de chaque renouvellement automatique. Après le premier mois, seules les conditions 
                  d'annulation standard s'appliquent.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.4 Exception au Droit de Rétractation</h3>
              <p className="mb-4">
                Si vous avez utilisé le service (envoi de SMS, ajout de clients, utilisation de fonctionnalités IA, etc.) 
                pendant la période de 14 jours, vous reconnaissez avoir expressément demandé que nous commencions 
                l'exécution du contrat avant la fin du délai de rétractation. Dans ce cas, vous perdez votre droit 
                de rétractation conformément à la législation applicable.
              </p>
            </section>

            {/* Exceptions */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Autres Cas de Remboursement</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.1 Facturation en Double</h3>
              <p className="mb-4">
                Si vous êtes facturé deux fois par erreur pour le même service au cours de la même période, 
                nous rembourserons intégralement le montant du double paiement dans un délai de 7 à 10 jours ouvrables.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.2 Erreur Technique de Notre Part</h3>
              <p className="mb-4">
                Si une erreur technique de notre système vous empêche d'accéder au service pendant une période 
                prolongée (plus de 7 jours consécutifs), nous pouvons, à notre discrétion, offrir un remboursement 
                au prorata ou un crédit équivalent.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.3 Circonstances Exceptionnelles</h3>
              <p>
                Dans des circonstances exceptionnelles (par exemple, problèmes médicaux graves, décès), nous examinerons 
                les demandes de remboursement au cas par cas. Des justificatifs pourront être demandés.
              </p>
            </section>

            {/* Credits */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Crédits SMS et Email</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.1 Crédits Non Remboursables</h3>
              <p className="mb-4">
                Les crédits SMS et email achetés (au-delà des 30 crédits gratuits initiaux) sont 
                <strong> non remboursables</strong>. Ces crédits restent disponibles sur votre compte 
                tant que votre abonnement est actif.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.2 Crédits Inutilisés à l'Annulation</h3>
              <p>
                Les crédits inutilisés au moment de l'annulation de votre abonnement sont perdus. 
                Assurez-vous d'utiliser vos crédits avant d'annuler.
              </p>
            </section>

            {/* How to Request */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Comment Demander un Remboursement</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">5.1 Processus de Demande</h3>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-4">
                <ol className="list-decimal list-inside space-y-3">
                  <li>Contactez notre support via votre tableau de bord Markeap</li>
                  <li>Indiquez clairement le motif de votre demande de remboursement</li>
                  <li>Fournissez votre ID de transaction et toute preuve pertinente</li>
                  <li>Attendez notre réponse dans un délai de 48 à 72 heures</li>
                </ol>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">5.2 Informations à Fournir</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Nom complet et email associé à votre compte</li>
                <li>Numéro de transaction ou date de facturation</li>
                <li>Raison détaillée de la demande de remboursement</li>
                <li>Captures d'écran ou preuves si applicable</li>
              </ul>
            </section>

            {/* Timeline */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Délais de Remboursement</h2>
              <p className="mb-4">
                Si votre demande de remboursement est approuvée :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Traitement :</strong> 5 à 7 jours ouvrables pour traiter la demande</li>
                <li><strong>Crédit bancaire :</strong> 7 à 10 jours ouvrables supplémentaires selon votre banque</li>
                <li><strong>Méthode :</strong> Le remboursement sera effectué sur le même moyen de paiement utilisé</li>
              </ul>
            </section>

            {/* No Refund */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cas Sans Remboursement</h2>
              <p className="mb-4">Aucun remboursement ne sera accordé dans les cas suivants :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Changement d'avis après avoir utilisé le service</li>
                <li>Non-utilisation du service par choix personnel</li>
                <li>Incompréhension des fonctionnalités (des démos sont disponibles avant inscription)</li>
                <li>Problèmes techniques de votre côté (connexion internet, appareil, etc.)</li>
                <li>Violation de nos Conditions d'Utilisation</li>
                <li>Après l'expiration de la période d'essai de 48 heures</li>
              </ul>
            </section>

            {/* Cancellation */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Annulation d'Abonnement</h2>
              <p className="mb-4">
                <strong>Important :</strong> Annuler votre abonnement n'équivaut PAS à un remboursement.
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Vous pouvez annuler votre abonnement à tout moment depuis votre tableau de bord</li>
                <li>L'annulation prend effet à la fin de votre période de facturation en cours</li>
                <li>Vous conservez l'accès aux fonctionnalités jusqu'à la fin de la période payée</li>
                <li>Aucun remboursement n'est accordé pour la période déjà payée</li>
              </ul>
            </section>

            {/* Disputes */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Litiges et Réclamations</h2>
              <p className="mb-4">
                Si vous n'êtes pas satisfait de notre décision concernant un remboursement, vous pouvez :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Demander une révision de votre cas en fournissant des informations supplémentaires</li>
                <li>Contacter votre banque pour une rétrofacturation (chargeback) - notez que cela peut entraîner 
                    la suspension de votre compte</li>
              </ul>
              <p className="text-sm text-gray-600">
                Nous vous encourageons à nous contacter directement avant d'initier une rétrofacturation, 
                car nous sommes généralement en mesure de résoudre les problèmes de manière amiable.
              </p>
            </section>

            {/* Changes */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Modifications de cette Politique</h2>
              <p>
                Nous nous réservons le droit de modifier cette Politique de Remboursement à tout moment. 
                Les modifications prendront effet immédiatement après leur publication. L'utilisation continue 
                du service après les modifications constitue votre acceptation de la nouvelle politique.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Nous Contacter</h2>
              <p className="mb-4">
                Pour toute question concernant cette Politique de Remboursement ou pour soumettre une demande :
              </p>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <p className="font-semibold text-gray-900">MY DIGITAL IDENTITY LTD (Markeap)</p>
                <p className="text-gray-700">Support Client - Demandes de Remboursement</p>
                <p className="text-gray-700">71-75 Shelton Street, Covent Garden</p>
                <p className="text-gray-700">London, United Kingdom, WC2H 9JQ</p>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Temps de réponse habituel : 48 à 72 heures ouvrables
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RefundPage;
